import { useId } from 'react';
import { cn } from '@/lib/utils';
import {
  FLOW_COLORS,
  FLOW_DROP,
  FLOW_LOCKUP,
  FLOW_TIDE,
  FLOW_WORDMARK,
  FLOW_WORD_KNOCKOUT,
} from './flowGlyph';

export interface FlowLogoProps {
  /** Rendered height in px; the width follows the lockup's own aspect ratio. */
  height?: number;
  /**
   * The wordmark's colour. `auto` follows the theme (navy in light, warm-50 in dark) through
   * `currentColor`; `ink` is always navy, for a ground known to be light; `light` is always
   * warm-50, for the chrome. The coin is the same navy on every ground.
   */
  tone?: 'auto' | 'ink' | 'light';
  className?: string;
  /** Accessible name — the wordmark is outlines, so this is what a screen reader gets. */
  title?: string;
}

/**
 * The horizontal lockup: the mark beside "flow." in Playfair Display Black with the grey full
 * stop, the face and colours Ember's "ember." uses. The paths and proportions come from
 * scripts/brand (the same as `public/brand/flow-logo-horizontal.svg`); the wordmark is outlines,
 * not text, so it renders the same everywhere and needs no font. The l is painted through a mask
 * that keeps its serif clear of the f's hook, so "fl" never closes into an h.
 */
export function FlowLogo({ height = 40, tone = 'auto', className, title = 'Flow' }: FlowLogoProps) {
  // Ids must be unique per instance, and url(#…) refs cannot contain the colons useId emits.
  const id = useId().replace(/[^a-zA-Z0-9_-]/g, '');
  const clip = `${id}-drop`;
  const mask = `${id}-fl`;
  const width = Math.round((height * FLOW_LOCKUP.width) / FLOW_LOCKUP.height);
  const ink = tone === 'light' ? FLOW_COLORS.inkOnDark : tone === 'ink' ? FLOW_COLORS.ink : 'currentColor';
  const dot = tone === 'light' ? FLOW_COLORS.dotOnDark : FLOW_COLORS.dot;

  return (
    <svg
      width={width}
      height={height}
      viewBox={FLOW_LOCKUP.viewBox}
      xmlns="http://www.w3.org/2000/svg"
      className={cn('shrink-0', tone === 'auto' && 'text-[#1b304e] dark:text-gray-50', className)}
      role="img"
      aria-label={title}
    >
      <g transform={`scale(${FLOW_LOCKUP.markScale})`}>
        <circle cx="256" cy="256" r="256" fill={FLOW_COLORS.navy} />
        <path d={FLOW_DROP} fill={FLOW_COLORS.cream} />
        <clipPath id={clip}>
          <path d={FLOW_DROP} />
        </clipPath>
        <g clipPath={`url(#${clip})`}>
          {FLOW_TIDE.map((band) => (
            <path key={band.fill} d={band.d} fill={band.fill} />
          ))}
        </g>
      </g>
      <g transform={FLOW_LOCKUP.wordTransform}>
        <mask id={mask} maskUnits="userSpaceOnUse" x="-50" y="-120" width="420" height="180">
          <rect x="-50" y="-120" width="420" height="180" fill="#fff" />
          <path
            d={FLOW_WORDMARK.f}
            fill="#000"
            stroke="#000"
            strokeWidth={2 * FLOW_WORD_KNOCKOUT}
            strokeLinejoin="round"
          />
        </mask>
        <path d={FLOW_WORDMARK.low} fill={ink} mask={`url(#${mask})`} />
        <path d={FLOW_WORDMARK.f} fill={ink} />
        <path d={FLOW_WORDMARK.dot} fill={dot} />
      </g>
    </svg>
  );
}
